import React from "react";
import { Flex, Tag, } from "antd";
import { Handle, Position } from "reactflow";
import { actionCodeToTitle, getActionIconAndClass } from "../utils/actionCodeMapping";
import { useWorkflowStore } from "../store";

export interface ActionNodeData {
    /** API action code (e.g. Action.Message.AutomatedResponse) or legacy short type */
    actionType?: string;
    /** API action code when using workflow enums */
    actionCode?: string;
    title?: string;
    isPlaceholder?: boolean;
    // AutomatedResponse
    message?: string;
    onceOnly?: boolean;
    responseType?: string;
    /** Template ID when a template is selected (for preview restore) */
    templateId?: string;
    /** Template display name (friendlyName) for node display */
    templateName?: string;
    /** Integration ID for template (needed for preview fetch on restore) */
    templateIntegId?: string;
    /** True when media files are attached (no template) */
    hasMedia?: boolean;
    /** Media files for display/restore when switching nodes */
    mediaFileList?: unknown[];
    /** Template components from preview (for WhatsApp templates) */
    components?: unknown;
    /** Template variable values (field name → value) */
    fieldNameAndValueMap?: Record<string, unknown>;
    // NotifyUser
    notificationTypes?: string[];

    notifyUser?: string;
    notifyUserLabel?: string;
    notifyMessage?: string;
    /** Include record owner in email notification */
    recordOwner?: boolean;
    /** Include account owner in email notification */
    accountOwner?: boolean;
    /** Include custom email addresses */
    includeAdditionalEmails?: boolean;
    /** Email addresses when "Additional Email Addresses" is selected */
    additionalEmails?: string[];
    // AddNote / LogActivity (keys aligned with settingConfig.js loggingPreference)
    /** Merge all activities into a single activity as notes? (true/false) */
    logRecordAsThread?: string;
    /** Where to log: "all" | "recent" */
    logRecordsFor?: string;

    delayDays?: number;
    delayHours?: number;
    delayMinutes?: number;
}

const ACTION_META: Record<string, { defaultTitle: string }> = {
    AutomatedResponse: { defaultTitle: "Automated Response" },
    NotifyUser: { defaultTitle: "Notify User" },
    CreateLead: { defaultTitle: "Create Lead" },
    LogActivity: { defaultTitle: "Log Activity" },
    AddNote: { defaultTitle: "Add Note" },
    Delay: { defaultTitle: "Delay" },
};

const DEFAULT_EMAIL_NOTIFICATION_MESSAGE =
    "You have received a new notification. Please check your account for details.";

const ACTION_CODE_TO_META_KEY: Record<string, string> = {
    "Action.Message.AutomatedResponse": "AutomatedResponse",
    "Action.Notify.NotifyUser": "NotifyUser",
    "Action.Crm.AddNote": "AddNote",
    "Action.Crm.LogActivity": "LogActivity",
    "Action.Crm.CreateLead": "CreateLead",
    "Action.FlowControl.Delay": "Delay",
};

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className=" d-flex align-items-center gap-2">
        <span className="hs-fs-12 hs-fw-400 hs-color-mediumDark">{label}</span>
        <Flex gap="small" align="center" className="hs-fs-12 hs-fw-400 hs-color-dark-grey flex-warp">{children}</Flex>
    </div>
);

const Badge: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
    <span className={`hs-fs-11 text-nowrap px-2 py-0 rounded-1 hs-color-BlackRussian ${className ?? ""}`.trim()}>{children}</span>
);

const DEFAULT_NODE_ICON = { icon: <div className="automationIconsSprite select-icon" /> };

function getNodeIcon(data: ActionNodeData) {
    if (!data.actionCode && !data.actionType) return DEFAULT_NODE_ICON;
    return getActionIconAndClass(data.actionCode ?? data.actionType);
}

function getActionFormType(data: ActionNodeData): string {
    if (data.actionCode && ACTION_CODE_TO_META_KEY[data.actionCode]) {
        return ACTION_CODE_TO_META_KEY[data.actionCode];
    }
    return data.actionType ?? "";
}

const renderActionDetails = (data: ActionNodeData, applyOncePerContact?: boolean | null) => {
    const formType = getActionFormType(data);
    switch (formType) {
        case "AutomatedResponse":
            return (
                <>
                    {data.templateName && (
                        <DetailRow label="Template:">
                            <Badge>{data.templateName}</Badge>
                        </DetailRow>
                    )}
                    {data.responseType && <DetailRow label="Response:"><Badge>{data.responseType}</Badge></DetailRow>}
                    {!data.templateName && data.message && (
                        <DetailRow label="Message:">
                            <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-mediumDark">&ldquo;{data.message}&rdquo;</span>
                        </DetailRow>
                    )}
                    {!data.templateName && !data.message && data.hasMedia && (
                        <DetailRow label="Message:">
                            <Badge>Media files attached</Badge>
                        </DetailRow>
                    )}
                    {(applyOncePerContact ?? data.onceOnly) && (
                        <DetailRow label="Apply once per contact:">
                            <Badge>Yes</Badge>
                        </DetailRow>
                    )}
                </>
            );
        case "NotifyUser":
            const recipients: string[] = [];
            if (data.recordOwner) recipients.push("Record Owner");
            if (data.accountOwner) recipients.push("Account Owner");
            if (data.includeAdditionalEmails && data.additionalEmails?.length) {
                recipients.push(...data.additionalEmails);
            }
            if (recipients.length === 0 && data.notifyUserLabel) {
                recipients.push(data.notifyUserLabel);
            }
            return (
                <>
                    {recipients.length > 0 && (
                        <DetailRow label="Send to:">
                            <Flex gap="small" align="center" wrap="wrap" className="hs-fs-12 hs-fw-400 hs-color-dark-grey">
                                {recipients.map((r) => (
                                    <Tag key={r} className="hs-fs-12 hs-fw-400 hs-color-dark-grey hs-bg-pale-blue-1 hs-border-md-grey rounded-2">{r}</Tag>
                                ))}
                            </Flex>
                        </DetailRow>
                    )}
                    <DetailRow label="Message:">
                        <span className="trigger-rule-node-detail-value-message overflow-hidden text-break flex-fill font-italic hs-color-mediumDark">
                            &ldquo;{data.notifyMessage ?? DEFAULT_EMAIL_NOTIFICATION_MESSAGE}&rdquo;
                        </span>
                    </DetailRow>
                </>
            );
        case "AddNote":
            return (
                <>
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <Badge>{data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}</Badge>
                        </DetailRow>
                    )}
                </>
            );
        case "LogActivity":
            return (
                <>
                    {data.logRecordAsThread && (
                        <DetailRow label="Merge:">
                            <Badge>{data.logRecordAsThread === "true" ? "Yes" : "No"}</Badge>
                        </DetailRow>
                    )}
                    {data.logRecordsFor && (
                        <DetailRow label="Log to:">
                            <Badge>{data.logRecordsFor === "recent" ? "Recent record" : "All matching records"}</Badge>
                        </DetailRow>
                    )}
                </>
            );
        case "Delay": {
            const d = data.delayDays ?? 0;
            const h = data.delayHours ?? 0;
            const m = data.delayMinutes ?? 0;
            const parts: string[] = [];
            if (d > 0) parts.push(`${d}d`);
            if (h > 0) parts.push(`${h}h`);
            if (m > 0) parts.push(`${m}m`);
            const label = parts.length > 0 ? parts.join(" ") : "—";
            return (
                <DetailRow label="Wait for:">
                    <Badge>{label}</Badge>
                </DetailRow>
            );
        }
        default:
            return null;
    }
};

function getActionMeta(data: ActionNodeData) {
    const key = data.actionCode
        ? ACTION_CODE_TO_META_KEY[data.actionCode] ?? data.actionType
        : data.actionType;
    return key ? ACTION_META[key] : null;
}

const ActionNodeComponent: React.FC<{ data: ActionNodeData; selected?: boolean }> = ({ data, selected }) => {
    const hasTypeSelected = !!(data.actionCode || data.actionType);
    const isPlaceholder = data.isPlaceholder ?? !hasTypeSelected;
    const meta = getActionMeta(data);
    const applyOncePerContact = useWorkflowStore((s) => s.applyOncePerContact);

    const displayTitle = isPlaceholder
        ? "Select an Action..."
        : (data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action");

    return (
        <div
            className={`hs-w-350-px position-relative hs-border-grey rounded-4 p-3 ${isPlaceholder ? "trigger-rule-node-placeholder hs-border-color-placeholder bg-transparent" : "hs-bg-plain-white"} ${selected ? "hs-border-violet" : ""}`}
            role="button"
            tabIndex={0}
            aria-label={`Action: ${displayTitle}. ${selected ? "Selected" : "Click to select"}`}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    (e.currentTarget as HTMLElement).click();
                }
            }}
        >
            <Handle type="target" position={Position.Top} id="target" className="opacity-0"/>
            <Handle type="source" position={Position.Bottom} id="source" className="opacity-0"/>
            <span className={`trigger-rule-node-tag hs-fs-11 hs-fw-600 rounded-3 border-1 hs-border-violet py-1 px-3 ${isPlaceholder ? "tag-fill" : "hs-color-violet hs-bg-plain-white"}`}>{isPlaceholder ? "+ NEW STEP" : "Action"}</span>

            {isPlaceholder ? (
                <div className="d-flex align-items-center gap-2">
                    <div className="d-flex align-items-center justify-content-center">
                        {getNodeIcon(data).icon}
                    </div>
                    <div className="d-flex flex-column gap-1 flex-grow-1 min-w-0">
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">Select an Action...</span>
                        <span className="hs-fs-12 hs-color-medium-dark">Choose from the panel on the right</span>
                    </div>
                </div>
            ) : (
                <>
                    <div className=" d-flex align-items-center gap-2">
                        <div className=" d-flex align-items-center justify-content-center">
                            {getNodeIcon(data).icon}
                        </div>
                        <span className="hs-fw-600 hs-color-BlackRussian hs-fs-12">
                            {data.title ?? meta?.defaultTitle ?? actionCodeToTitle(data.actionCode) ?? "Action"}
                        </span>
                    </div>
                    <Flex vertical gap="small" justify="space-between" className="py-3">{renderActionDetails(data, applyOncePerContact)}</Flex>
                </>
            )}
        </div>
    );
};

export const ActionNode = React.memo(ActionNodeComponent);
